<script lang="ts">
	import type { SbImage } from '../../types';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import CustomerReview from '$lib/storyblok/components/customerReview.svelte';
	import type { Review, SortByType, SortDirection, SortType } from '$lib/types/yotpo';
	import SortByDropDown from '$lib/storyblok/parts/reviews/sortByDropDown.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import StarRating from '$lib/review/starRating.svelte';
	import { slideIn } from '$lib/actions/slideIn';
	import Button from '$lib/buttons/v3/button.svelte';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import { getYotpoPreview, type YotpoPreview } from '$lib/review/preview';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Yotpo from '$lib/icons/yotpo.svelte';

	interface Props {
		content: ISbStoryData<{
			image: SbImage;
			image_text: ISbRichtext;
			title: string;
		}>['content'];
	}

	let { content }: Props = $props();

	const ratingThreshold = 5;

	let preview = $state<YotpoPreview>();
	let reviews = $state<Array<Review>>([]);
	let maxIndex = $state(5);
	let page = $state(1);
	let hasMore = $state(true);
	let loading = $state(false);

	let sort: SortByType = 'date';
	let sortDirection: SortDirection = 'desc';

	async function fetchReviews() {
		loading = true;
		try {
			const res = await fetch(
				`/api/yotpo/reviews?star=${ratingThreshold}&sort=${sort}&direction=${sortDirection}&page=${page}`
			);
			const response = (await res.json()) as {
				reviews: Review[];
				hasMore: boolean;
			};

			reviews = [...reviews, ...response.reviews];
			hasMore = response.hasMore;
			page++;
		} finally {
			loading = false;
		}
	}

	async function showMore(amount = 5) {
		maxIndex += amount;
		if (maxIndex >= reviews.length - amount) {
			const reviewsBefore = reviews.length;
			await fetchReviews();
			if (reviewsBefore === reviews.length) {
				hasMore = false;
			}
		}
	}

	function sortRatings(sortBy: SortType) {
		maxIndex = 5;

		let sortMethod: (a: Review, b: Review) => number;

		switch (sortBy) {
			case 'createdAtAsc':
				sort = 'date';
				sortDirection = 'asc';
				sortMethod = (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
				break;
			case 'createdAtDesc':
				sort = 'date';
				sortDirection = 'desc';
				sortMethod = (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
				break;
			case 'ratingHighest':
				sort = 'rating';
				sortDirection = 'desc';
				sortMethod = (a, b) => b.score - a.score;
				break;
		}

		if (sortMethod) {
			reviews = reviews.sort(sortMethod);
		}
	}
</script>

<section
	id="reviews-widget"
	use:editable={content}
	class="bg-white px-5 pb-10 text-brown-1 lg:pb-16"
	use:checkVisibility={{ threshold: 0 }}
	onvisible={async ({ detail }) => {
		if (!detail || reviews.length > 0) return;

		fetchReviews();
		preview = await getYotpoPreview();
	}}
>
	<PageGrid16>
		<div class="relative col-span-full mb-8 mt-8 lg:mb-14 lg:mt-16">
			{#if content.image?.filename}
				<Image
					class="aspect-videoc relative h-full w-full rounded-3xl object-cover"
					src={content.image.filename}
					alt={content.image.alt}
					title={content.image.title}
					width={800}
					sizes={{ 1024: { width: 2500 } }}
				/>
				<div
					use:slideIn={{ threshold: 0.5 }}
					class="absolute left-0 top-0 p-20 text-[72px]/[76px] tracking-[-.72px] text-white max-lg:hidden"
				>
					<SbRichtext text={content.image_text} />
				</div>
			{/if}
		</div>
		<div class="col-span-full lg:col-start-2 lg:col-end-7">
			<div class="lg:sticky lg:pt-4" style="top: calc(var(--full-header-height) + 10px);">
				<div class="flex items-center gap-x-2">
					<p
						class="text-[12px]/[140%] font-semibold uppercase tracking-[.96px] text-brown-1/60 lg:text-[14px] lg:tracking-[1.12px]"
					>
						Trusted reviews by
					</p>

					<Yotpo class="h-4 text-[#0042E4]" />
				</div>
				<h1
					class="mt-3 text-[36px]/[112%] font-semibold tracking-[-0.36px] lg:mt-4 lg:text-[48px]/[110%] lg:tracking-[-0.48px]"
				>
					{content.title}
				</h1>

				<div class="mt-4 flex items-center gap-x-4">
					{#if preview}
						<p
							class="text-[80px]/[100%] tracking-[-1.6px] lg:mt-3 lg:text-[88px] lg:tracking-[-1.76px]"
						>
							{preview.averageScore}<span class="max-lg:hidden">/5</span>
						</p>
						<div class="flex flex-col justify-center gap-y-2 text-[14px]/[160%] lg:hidden">
							<StarRating score={5} />
							<p>Based on {preview.totalReviews} reviews</p>
						</div>
					{/if}
				</div>
			</div>
		</div>

		<div class="col-span-full flex flex-col gap-y-11 lg:col-start-9 lg:col-end-[15]">
			<div class="flex justify-between text-[14px]/[160%] max-lg:mt-4">
				{#if preview}
					<p class="max-lg:hidden">Based on {preview.totalReviews} reviews</p>
				{/if}

				<SortByDropDown
					onselect={(type) => {
						sortRatings(type);
					}}
				/>
			</div>
			<div class="flex flex-col gap-y-12" class:min-h-[80vh]={loading}>
				{#each Array(maxIndex) as _, index}
					{#if index < reviews.length}
						<CustomerReview review={reviews[index]} />
					{/if}
				{/each}
			</div>

			<div class="mt-4 lg:flex">
				{#if !loading}
					<Button class="btn-brown" disabled={!hasMore} onclick={async () => await showMore()}
						>{hasMore ? 'Show more' : 'No more reviews available'}</Button
					>
				{/if}
			</div>
		</div>
	</PageGrid16>
</section>
